import { BasicOptions, TimeInterval, TTLOptions } from "savant-connector";
import { ScreenSize } from "../console/ScreenSizeContext";

export const MainTargetId = "main";

export const enum MenuStyleMode {
    FlyOut = "flyout",
    Docking = "docking",
}

type ItemCountPerResolution = { [screenSize in ScreenSize]: number };

export const PerPageItemsCount: ItemCountPerResolution = {
  XL: 18,
  L: 15,
  M: 12,
  S: 8,
  XS: 6,
};

export const SubListingPageItemCount: ItemCountPerResolution = {
  XL: 10,
  L: 8,
  M: 6,
  S: 4,
  XS: 2,
};

export const SubDetailsListingItemCount: ItemCountPerResolution = {
  XL: 8,
  L: 6,
  M: 4,
  S: 2,
  XS: 2,
};

export const topHeaderHeight = 40;
export const footerHeight = 30;

export const notificationWidth = 340;
export const notificationGap = 5;
export const notificationTimeout = 6000; // in msecs

export const ttlFiveSecCaching: TTLOptions = { type: "ttl", refreshIfOlderThan: TimeInterval.sm };
export const ttlTenSecCaching: TTLOptions = { type: "ttl", refreshIfOlderThan: TimeInterval.md };
export const ttlOneMinCaching: TTLOptions = { type: "ttl", refreshIfOlderThan: TimeInterval.lg };
export const ttlFiveMinCaching: TTLOptions = { type: "ttl", refreshIfOlderThan: TimeInterval.xl };
export const ttlTenMinCaching: TTLOptions = { type: "ttl", refreshIfOlderThan: TimeInterval.xxl };
export const basicNoRefreshCaching: BasicOptions = { type: "basic", refreshOnMount: false };

export enum MonochromeIconIds {
  CheckMark = "CheckMark",
  Copy = "copy",
  Create = "Add",
  Database = "Database",
  Delete = "Delete",
  Disable = "Cancel",
  Download = "Download",
  Edit = "Edit",
  Enable = "CheckMark",
  Help = "Help",
  List = "List",
  OpenInNewWindow = "OpenInNewWindow",
  PageRemove = "PageRemove",
  Permissions = "Permissions",
  Play = "Play",
  Refresh = "Refresh",
  Restart = "PlaybackRate1x",
  Rotate = "Rotate",
  Rotate90CounterClockwise = "Rotate90CounterClockwise",
  Scale = "ScaleVolume",
  Search = "Search",
  StatusCircleIcon = "StatusCircleBlock2",
  Stop = "Stop",
  Up = "Up",
  Upload = "Upload",
  ScaleUp = "ScaleUp",
}

export enum SvgIconIds {
  homeSvg = "HomeSolid",
  addSvg = "add-svg",
  apexSvg = "apex-svg",
  mysqlSvg = "mysql-svg",
  heatWaveSvg = "heatwave-svg",
  tagsSvg = "tags-svg",
  backupsSvg = "backups-svg",
  dbSystemSvg = "dbSystem-svg",
  baseDbSvg = "base-database-svg",
  databaseSvg = "database-svg",
  pluggableSvg = "pluggable-svg",
  adbSvg = "adb-svg",
  infraSvg = "infra-svg",
  vmClusterSvg = "vmcluster-svg",
  attachedDbSvg = "attachedDb-svg",
  networkingSvg = "networking-svg",
  connectSvg = "connect-svg",
  configurationSvg = "configuration-svg",
  securitySvg = "security-svg",
  managementSvg = "management-svg",
  databaseNodesSvg = "databaseNodes-svg",
  exadataSvg = "exadata-svg",
  exaSvg = "exa-svg",
  supportSvg = "support-svg",
  supportWaterMarkSvg = "support-watermark-svg",
  networkSvg = "network-svg",
  networkWaterMarkSvg = "Network-watermark-svg",
  apexWatermarkSvg = "apex-watermark-svg",
  adbWatermarkSvg = "adb-watermark-svg",
  baseDbWatermarkSvg = "base-database-watermark-svg",
  exaWaterMarkSvg = "exa-watermark-svg",
  infraWaterMarkSvg = "infra-watermark-svg",
  vmClusterWaterMarkSvg = "vmcluster-watermark-svg",
  mySqlWaterMarkSvg = "mysql-watermark-svg",
  deploymentSvg = "deployment-detail-svg",
  deploymentWaterMarkSvg = "deployment-watermark-svg",
  subscriptionSvg = "subscription-svg",
  billingSvg = "billing-svg",
}

export enum GroupingKeys {
  NoGrouping = "noGrouping"
}

export enum ViewKeys {
  ListView = "listView"
}

export const metaItemLabelsGap = 5;

export const emptyChar = "-";

export const testIdAttribute = "data-test-id";
export const valueLinkTestIdSuffix = "-link";
export const emptyDivTestId = "empty-div-test-id";
export const spinnerTestId = "spinner-test-id";

export enum ListingTestIds {
  Adbs = "adbs-list",
  AdbsBackups = "adbs-backups-list",
  AdbsConnect = "adbs-connect-list",
  Deployments = "deployments-list",
  ExaDbBackups = "exa-db-backups-list",
  ExaDbConnect = "exa-db-connect-list",
  ExaDbPdbs = "exa-db-pdbs-list",
  ExaDb = "exa-db-list",
  ExaInfra = "exa-infra-list",
  ExaInfraVmClusters = "exa-infra-vm-clusters-list",
  ExaVmCluster = "exa-vm-cluster-list",
  ExaVmClusterDatabases = "exa-vm-cluster-databases-list",
  ExaVmClusterDbHomes = "exa-vm-cluster-db-homes-list",
  ExaVmClusterDbNodes = "exa-vm-cluster-db-nodes-list",
  ExaVmClusterSshKeys = "exa-vm-cluster-ssh-keys-list",
  McvcnAttachedDbs = "mcvcn-attached-db-list",
  Mcvcn = "mcvcn-list",
  MysqlBackups = "mysql-backups-list",
  Mysql = "mysql-list",
  Support = "support-list",
  VmDbBackups = "vmdb-backups-list",
  VmDbConnect = "vmdb-connect-list",
  VmDbPdbs = "vmdb-pdbs-list",
  VmDb = "vmdb-list",
  VmDbSystemDatabases = "vmdb-system-databases-list",
  VmDbSystemDbHomes = "vmdb-system-db-homes-list",
  VmDbSystemDbNodes = "vmdb-system-db-nodes-list",
  VmDbSystemSshKeys = "vmdb-system-ssh-keys-list"
}

export enum CalloutTestIds {
  VmDbSystemEditLicense = "vmdb-system-edit-license-callout",
  VmDbSystemEditStorage = "vmdb-system-edit-storage-callout",
  VmDbSystemEditScale = "vmdb-system-edit-scale-callout",
  BackupCreatePanel = "backup-create-panel-callout",
  UpdatePassword = "update-password-callout",
  BackupRestorePanel = "backup-restore-callout",
}

export enum SidePanelTestIds {
  VmDbCdbBackupEditPanel = "vmdb-cdb-backup-edit-side-panel",
  VmDbPdbCreatePanel = "vmdb-pdb-create-side-panel",
  AddSshKey = "add-ssh-key",
}

export enum ModalTestIds {
  PrimaryButton = "test-primary-btn",
  CancelButton = "test-cancel-btn",
  InfoBlockText = "test-info-block-text",
}

export enum CreateWizardTestIds {
  Adbs = "adbs-create",
}
